import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { AuthModalShell } from './components/AuthModalShell'

// Auth pages
import { LoginScreen } from './pages/LoginScreen'
import { SignupScreen } from './pages/SignupScreen'
import { ForgotPasswordScreen } from './pages/ForgotPasswordScreen'
import { ResetPasswordScreen } from './pages/ResetPasswordScreen'

// Main pages
import { LandingScreen } from './pages/LandingScreen'
import { TrustScreen } from './pages/TrustScreen'
import { WorkspaceScreen } from './pages/WorkspaceScreen'
import { ImportScreen } from './pages/ImportScreen'
import { ImportSourcePreviewScreen } from './pages/ImportSourcePreviewScreen'
import { StudioScreen } from './pages/StudioScreen'
import { CapabilitySettingsScreen } from './pages/CapabilitySettingsScreen'
import { PricingEngineScreen } from './pages/PricingEngineScreen'
import { TaskCenterScreen } from './pages/TaskCenterScreen'

// Settings pages
import { GeneralInfoScreen } from './pages/settings/GeneralInfoScreen'
import { SecurityScreen } from './pages/settings/SecurityScreen'
import { BillingScreen } from './pages/settings/BillingScreen'
import { SubscriptionScreen } from './pages/settings/SubscriptionScreen'
import { PurchaseCreditsScreen } from './pages/settings/PurchaseCreditsScreen'
import { PlatformPreferencesScreen } from './pages/settings/PlatformPreferencesScreen'
import { NotificationsScreen } from './pages/settings/NotificationsScreen'
import { IntegrationsScreen } from './pages/settings/IntegrationsScreen'
import { BindStoreScreen } from './pages/BindStoreScreen'
import { ConnectStoreScreen } from './pages/settings/ConnectStoreScreen'
import { AssetManagementScreen } from './pages/AssetManagementScreen'
import { AdminDashboardScreen } from './pages/admin/AdminDashboardScreen'
import { AdminAIChannelsScreen } from './pages/admin/AdminAIChannelsScreen'
import { AdminAIChannelMonitorScreen } from './pages/admin/AdminAIChannelMonitorScreen'
import { AdminBillingScreen } from './pages/admin/AdminBillingScreen'
import { AdminConsumptionScreen } from './pages/admin/AdminConsumptionScreen'
import { AdminUsersScreen } from './pages/admin/AdminUsersScreen'
import { AdminLogsScreen } from './pages/admin/AdminLogsScreen'
import { AdminSettingsScreen } from './pages/admin/AdminSettingsScreen'

const AuthModalRouteShell: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = new URLSearchParams(location.search)
  const rawMode = params.get('auth')
  const mode = rawMode === 'login' || rawMode === 'signup' || rawMode === 'forgot' || rawMode === 'reset'
    ? rawMode
    : null

  const handleClose = () => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.delete('auth')
    const nextSearch = nextParams.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    )
  }

  const handleSwitch = (nextMode: 'login' | 'signup' | 'forgot' | 'reset') => {
    const nextParams = new URLSearchParams(location.search)
    nextParams.set('auth', nextMode)
    const nextSearch = nextParams.toString()
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : ''
      },
      { replace: true }
    )
  }

  return (
    <AuthModalShell mode={mode} onClose={handleClose} onSwitch={handleSwitch}>
      <Outlet />
    </AuthModalShell>
  )
}

// Copyright pages
import { CopyrightDashboardScreen } from './pages/copyright/CopyrightDashboardScreen'
import { CopyrightLetterScreen } from './pages/copyright/CopyrightLetterScreen'
import { CopyrightPSDScreen } from './pages/copyright/CopyrightPSDScreen'
import { CopyrightWatermarkScreen } from './pages/copyright/CopyrightWatermarkScreen'
import { CopyrightBlockchainScreen } from './pages/copyright/CopyrightBlockchainScreen'
import { CopyrightDisputeScreen } from './pages/copyright/CopyrightDisputeScreen'

export const router = createBrowserRouter([
  {
    element: <AuthModalRouteShell />,
    children: [
      // Public routes
      {
        path: '/',
        element: <LandingScreen />,
      },
      {
        path: '/trust',
        element: <TrustScreen />,
      },

      // Auth routes
      {
        path: '/login',
        element: <LoginScreen />,
      },
      {
        path: '/signup',
        element: <SignupScreen />,
      },
      {
        path: '/forgot-password',
        element: <ForgotPasswordScreen />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordScreen />,
      },

      // App routes (will add auth guard later)
      {
        path: '/workspace',
        element: <WorkspaceScreen />,
      },
      {
        path: '/task-center',
        element: <TaskCenterScreen />,
      },
      {
        path: '/import',
        element: <ImportScreen />,
      },
      {
        path: '/import/preview',
        element: <ImportSourcePreviewScreen />,
      },
      {
        path: '/studio',
        element: <StudioScreen />,
      },
      {
        path: '/assets',
        element: <AssetManagementScreen />,
      },
      {
        path: '/import',
        element: <ImportScreen />,
      },
      {
        path: '/capability-settings',
        element: <CapabilitySettingsScreen />,
      },
      {
        path: '/pricing',
        element: <PricingEngineScreen />,
      },

      // Settings routes
      {
        path: '/settings',
        element: <Navigate to="/settings/general" replace />,
      },
      {
        path: '/settings/general',
        element: <GeneralInfoScreen />,
      },
      {
        path: '/settings/security',
        element: <SecurityScreen />,
      },
      {
        path: '/settings/billing',
        element: <BillingScreen />,
      },
      {
        path: '/settings/subscription',
        element: <SubscriptionScreen />,
      },
      {
        path: '/settings/credits',
        element: <PurchaseCreditsScreen />,
      },
      {
        path: '/settings/preferences',
        element: <PlatformPreferencesScreen />,
      },
      {
        path: '/settings/notifications',
        element: <NotificationsScreen />,
      },
      {
        path: '/settings/integrations',
        element: <IntegrationsScreen />,
      },
      {
        path: '/stores/bind',
        element: <BindStoreScreen />,
      },

      // Admin routes
      {
        path: '/admin',
        element: <AdminDashboardScreen />,
      },
      {
        path: '/admin/ai-channels',
        element: <AdminAIChannelsScreen />,
      },
      {
        path: '/admin/ai-channels/monitor',
        element: <AdminAIChannelMonitorScreen />,
      },
      {
        path: '/admin/billing',
        element: <AdminBillingScreen />,
      },
      {
        path: '/admin/consumption',
        element: <AdminConsumptionScreen />,
      },
      {
        path: '/admin/users',
        element: <AdminUsersScreen />,
      },
      {
        path: '/admin/logs',
        element: <AdminLogsScreen />,
      },
      {
        path: '/admin/settings',
        element: <AdminSettingsScreen />,
      },

      // Copyright routes
      {
        path: '/copyright',
        element: <Navigate to="/copyright/dashboard" replace />,
      },
      {
        path: '/copyright/dashboard',
        element: <CopyrightDashboardScreen />,
      },
      {
        path: '/copyright/letter',
        element: <CopyrightLetterScreen />,
      },
      {
        path: '/copyright/psd',
        element: <CopyrightPSDScreen />,
      },
      {
        path: '/copyright/watermark',
        element: <CopyrightWatermarkScreen />,
      },
      {
        path: '/copyright/blockchain',
        element: <CopyrightBlockchainScreen />,
      },
      {
        path: '/copyright/dispute',
        element: <CopyrightDisputeScreen />,
      },

      // Catch all - redirect to workspace
      {
        path: '*',
        element: <Navigate to="/workspace" replace />,
      },
    ]
  }
])
